import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '../utils.ts';

type Variant = 'primary' | 'secondary' | 'ghost' | 'inverse';
type Size = 'md' | 'lg';

interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
  children: ReactNode;
}

const base =
  'inline-flex items-center justify-center gap-2 rounded-button font-semibold ' +
  'transition-all duration-fast ease-clickt ' +
  'focus-visible:outline-none focus-visible:shadow-focus-ring ' +
  'disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ' +
  'motion-reduce:transform-none';

const variants: Record<Variant, string> = {
  primary:
    'bg-electric text-white hover:bg-sapphire hover:-translate-y-px ' +
    'active:translate-y-0 shadow-soft hover:shadow-hover',
  secondary:
    'bg-white text-midnight border border-midnight/10 hover:border-midnight/30 ' +
    'hover:-translate-y-px',
  ghost: 'bg-transparent text-midnight hover:bg-light-silver',
  inverse: 'bg-white text-midnight hover:bg-light-silver hover:-translate-y-px shadow-soft',
};

const sizes: Record<Size, string> = {
  md: 'h-12 px-5 text-[15px]',
  lg: 'h-14 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', asChild = false, className, children, ...rest },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref as never}
      className={cn(base, variants[variant], sizes[size], className)}
      {...rest}
    >
      {children}
    </Comp>
  );
});
